
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import WLSTableFilterable from '@/components/reports-v3/components/filterables/Customize/WLSTableFilterable.vue';
import { PermissionsGroup } from '@/store/models.def';
import { TableItemFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import moment from 'moment';
import gdbx from '@/store/modules/gdbx';
import { addComma } from '@/util/number';
import TableCollapseToggle from '@/components/TableCollapseToggle.vue';
import HighlightAccountsToggle from '@/components/HighlightAccountsToggle.vue';
import settingx from '@/store/modules/settingx';
import Checkbox from 'primevue/checkbox';
import { getTableName } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		WLSTableFilterable,
		TableCollapseToggle,
		HighlightAccountsToggle,
		Checkbox,
	},
})
export default class CustomerOutstandingDetailsAllTable extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['customers'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public tableItems: any[] = [];
	public tableFields: any[] = [];
	public detailFields: any[] = [];

	public isApplyPdc: boolean = false;
	public isShowCreditTerm: boolean = true;
	public isShowDueDetails: boolean = false;

	public periodTotalData: any[] = [];
	public periodTotalFields: any[] = [
		{
			key: 'current_sum',
			label: 'Sum of Current Amount',
			textAlign: 'center',
		},
		{
			key: 'due_sum',
			label: 'Sum of Due Amount',
			textAlign: 'center',
		},
		{
			key: 'unapplied_sum',
			label: 'Sum of Unapplied Amount',
			textAlign: 'center',
		},
		{
			key: 'outstanding_sum',
			label: 'Sum of Total Outstanding',
			textAlign: 'center',
		},
	];

	public dueSum: number = 0;
	public currentSum: number = 0;
	public outstandingSum: number = 0;
	public unappliedSum: number = 0;

	public format(
		value: number,
		decimal: number = 2,
		forceDecimal: boolean = true,
	) {
		return addComma(value, decimal, forceDecimal);
	}

	public get exportFileName() {
		const formattedDate = moment(this.selectedAsOfDate).format('DD MMM YY');

		return 'Customer Outstanding - Details' + '_' + 'As Of ' + formattedDate;
	}

	public get isHighlightAccount() {
		return settingx.now.highlightAccount;
	}

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get dateFormatted() {
		return [this.selectedAsOfDate];
	}

	public get updatedTable() {
		const tableFieldClone = [...this.tableFields];

		const creditPosition = tableFieldClone.findIndex(
			(field) => field.key === 'credit_term',
		);

		const firstDuePosition = tableFieldClone.findIndex(
			(field) => field.key === 'due_less_30',
		);

		const totalDuePosition = tableFieldClone.findIndex(
			(field) => field.key === 'due_amount',
		);

		if (!this.isShowCreditTerm) {
			tableFieldClone.splice(creditPosition, 1);
		}

		if (!this.isShowDueDetails) {
			tableFieldClone.splice(firstDuePosition, 5);
		}

		if (this.isShowDueDetails) {
			tableFieldClone.splice(totalDuePosition, 1);
		}

		return tableFieldClone;
	}

	public get expensiveHook() {
		const {
			selectedAsOfDate,
			selectedAgents,
			selectedCustomers,
			isApplyPdc,
			isHighlightAccount,
		} = this;
		return JSON.stringify([
			selectedAsOfDate,
			selectedAgents,
			selectedCustomers,
			isApplyPdc,
			isHighlightAccount,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.35);

		const asOfDate = this.selectedAsOfDate;
		const nowMs = moment().valueOf();
		const msPerDay = 86400000; // 24 * 60 * 60 * 1000

		// SQL day diff calculation matching moment().diff(x, 'day') behavior
		// FLOOR gives same result for positive values; for negative, both are <= 0 so treated as "current"
		const dayDiff = (dueDateCol: string) =>
			`FLOOR((${asOfDate} - TRY_CAST(${dueDateCol} AS BIGINT)) / ${msPerDay}.0)`;

		// Build manual filter clauses
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		let customerFilter = '';
		if (this.selectedCustomers.length > 0) {
			const list = this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
			customerFilter = ` AND "CODE" IN (${list})`;
		}
		let agentFilter = '';
		if (this.selectedAgents.length > 0) {
			const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
			agentFilter = ` AND "AGENT" IN (${list})`;
		}

		const ivdnTable = getTableName('customerIvDns');
		const koTable = getTableName('customerKos');
		const tradeTable = getTableName('customerTrades');
		const globalTable = getTableName('diyGlobalCustomers');

		const queryHead = await this._ensureTablesLoaded([
			'customerIvDns', 'customerKos', 'customerTrades', 'diyGlobalCustomers',
		] as any[]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.35, 0.9);

		// Run all data queries in parallel
		const [ivdnRows, koRows, tradeRows, globalRows] = await Promise.all([
			// IVDN documents
			queryHead.rawQuery(`
				SELECT "CODE" as code, "DOCNO" as docNo,
					TRY_CAST("DOCDATE" AS BIGINT) as date,
					TRY_CAST("DUEDATE" AS BIGINT) as dueDate,
					TRY_CAST("LOCALDOCAMT" AS DOUBLE) as amount
				FROM ${ivdnTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${asOfDate}
					${customerFilter}${agentFilter}
			`),
			// KO documents (all - both PDC and non-PDC)
			queryHead.rawQuery(`
				SELECT "CODE" as code, "TODOCNO" as toDocNo,
					TRY_CAST("TODOCDUEDATE" AS BIGINT) as dueDate,
					TRY_CAST("KOLOCALAMT" AS DOUBLE) as amount,
					TRY_CAST("KOGAINLOSS" AS DOUBLE) as gainLoss,
					TRY_CAST("FROMDOCDATE" AS BIGINT) as koDate
				FROM ${koTable}
				WHERE TRY_CAST("TODOCDATE" AS BIGINT) <= ${asOfDate}
					${customerFilter}${agentFilter}
			`),
			// Trade (unapplied amounts)
			queryHead.rawQuery(`
				SELECT "CODE" as code,
					TRY_CAST("UNAPPLIEDAMT" AS DOUBLE) as unappliedAmount
				FROM ${tradeTable}
				WHERE 1=1${customerFilter}${agentFilter}
			`),
			// Global customers (credit terms)
			queryHead.rawQuery(`
				SELECT "CODE" as code, "CREDITTERM" as creditTerm
				FROM ${globalTable}
				WHERE 1=1${customerFilter}
			`),
		]);
		this.checkInterrupted(jobId);

		// Group data by customer code
		const groupedIvdn: Record<string, any[]> = {};
		for (const row of ivdnRows) {
			if (!groupedIvdn[row.code]) { groupedIvdn[row.code] = []; }
			groupedIvdn[row.code].push({
				code: row.code,
				docNo: row.docNo,
				date: Number(row.date) || 0,
				dueDate: Number(row.dueDate) || 0,
				amount: Number(row.amount) || 0,
			});
		}

		const groupedKo: Record<string, any[]> = {};
		for (const row of koRows) {
			if (!groupedKo[row.code]) { groupedKo[row.code] = []; }
			groupedKo[row.code].push({
				code: row.code,
				toDocNo: row.toDocNo,
				dueDate: Number(row.dueDate) || 0,
				amount: Number(row.amount) || 0,
				gainLoss: Number(row.gainLoss) || 0,
				koDate: Number(row.koDate) || 0,
			});
		}

		const groupedTrade: Record<string, any[]> = {};
		for (const row of tradeRows) {
			if (!groupedTrade[row.code]) { groupedTrade[row.code] = []; }
			groupedTrade[row.code].push({
				unappliedAmount: Number(row.unappliedAmount) || 0,
			});
		}

		const groupedGlobal: Record<string, any> = {};
		for (const row of globalRows) {
			groupedGlobal[row.code] = { creditTerm: row.creditTerm || '' };
		}

		this.generateLoadingText(0.95);

		// Process each customer - same logic as original
		const result: any[] = [];

		for (const code in groupedIvdn) {
			if (!groupedIvdn.hasOwnProperty(code)) { continue; }

			const cards = groupedIvdn[code];
			const koCards2 = groupedKo[code] || [];
			const tradeCards = groupedTrade[code] || [];

			// Calculate dueSum2 for highlightAccount filter
			// Uses ALL KOs (both PDC and non-PDC) - matching original logic
			let dueSum2 = 0;
			for (const ivdn of cards) {
				const days = Math.floor((asOfDate - ivdn.dueDate) / msPerDay);
				if (days > 0) {
					dueSum2 += ivdn.amount;
				}
			}
			for (const ko of koCards2) {
				const days = Math.floor((asOfDate - ko.dueDate) / msPerDay);
				if (days > 0) {
					dueSum2 -= ko.amount + ko.gainLoss;
				}
			}

			// Skip if highlightAccount enabled and no due amount
			if (this.isHighlightAccount && Math.round(dueSum2) === 0) { continue; }

			const object: any = {
				company_name: gdbx.customerNames[code] || code,
				current_amount: 0,
				due_less_30: 0,
				due_less_60: 0,
				due_less_90: 0,
				due_less_120: 0,
				due_more_120: 0,
				due_amount: 0,
				total_outstanding: 0,
				total_pdc: 0,
				unapplied_amount: 0,
				credit_term: '',
				_cellVariants: {} as Record<string, string>,
				items: [] as any[],
			};

			// Unapplied amount
			const unappliedAmount = tradeCards.reduce(
				(a, b) => a + (b.unappliedAmount || 0),
				0,
			);
			object.unapplied_amount = unappliedAmount;

			let totalOutstanding = 0;

			// Process IVDN cards - aging buckets
			for (const ivdn of cards) {
				const days = Math.floor((asOfDate - ivdn.dueDate) / msPerDay);

				if (days <= 0) {
					object.current_amount += ivdn.amount;
					totalOutstanding += ivdn.amount;
				} else if (days > 0 && days <= 30) {
					object.due_less_30 += ivdn.amount;
					object.due_amount += ivdn.amount;
					totalOutstanding += ivdn.amount;
				} else if (days > 30 && days <= 60) {
					object.due_less_60 += ivdn.amount;
					object.due_amount += ivdn.amount;
					totalOutstanding += ivdn.amount;
				} else if (days > 60 && days <= 90) {
					object.due_less_90 += ivdn.amount;
					object.due_amount += ivdn.amount;
					totalOutstanding += ivdn.amount;
				} else if (days > 90 && days <= 120) {
					object.due_less_120 += ivdn.amount;
					object.due_amount += ivdn.amount;
					totalOutstanding += ivdn.amount;
				} else {
					object.due_more_120 += ivdn.amount;
					object.due_amount += ivdn.amount;
					totalOutstanding += ivdn.amount;
				}
			}

			// Process KO cards - subtract non-PDC (koDate <= now)
			for (const ko of koCards2) {
				if (ko.koDate > nowMs) { continue; } // Skip PDC for now

				const days = Math.floor((asOfDate - ko.dueDate) / msPerDay);
				const koTotal = ko.amount + ko.gainLoss;

				if (days <= 0) {
					object.current_amount -= koTotal;
					totalOutstanding -= koTotal;
				} else if (days > 0 && days <= 30) {
					object.due_less_30 -= koTotal;
					object.due_amount -= koTotal;
					totalOutstanding -= koTotal;
				} else if (days > 30 && days <= 60) {
					object.due_less_60 -= koTotal;
					object.due_amount -= koTotal;
					totalOutstanding -= koTotal;
				} else if (days > 60 && days <= 90) {
					object.due_less_90 -= koTotal;
					object.due_amount -= koTotal;
					totalOutstanding -= koTotal;
				} else if (days > 90 && days <= 120) {
					object.due_less_120 -= koTotal;
					object.due_amount -= koTotal;
					totalOutstanding -= koTotal;
				} else {
					object.due_more_120 -= koTotal;
					object.due_amount -= koTotal;
					totalOutstanding -= koTotal;
				}
			}

			object.total_outstanding = totalOutstanding - unappliedAmount;

			// Apply PDC if enabled
			if (this.isApplyPdc) {
				let totalPdcCurrent = 0;
				let totalPdcLess30 = 0;
				let totalPdcLess60 = 0;
				let totalPdcLess90 = 0;
				let totalPdcLess120 = 0;
				let totalPdcMore120 = 0;

				for (const ko of koCards2) {
					if (ko.koDate < nowMs) { continue; } // Only PDC (koDate >= now)

					const days = Math.floor((asOfDate - ko.dueDate) / msPerDay);
					const koTotal = ko.amount + ko.gainLoss;

					if (days <= 0) {
						totalPdcCurrent += koTotal;
					} else if (days > 0 && days <= 30) {
						totalPdcLess30 += koTotal;
					} else if (days > 30 && days <= 60) {
						totalPdcLess60 += koTotal;
					} else if (days > 60 && days <= 90) {
						totalPdcLess90 += koTotal;
					} else if (days > 90 && days <= 120) {
						totalPdcLess120 += koTotal;
					} else {
						totalPdcMore120 += koTotal;
					}
				}

				const totalPdcDue =
					totalPdcLess30 + totalPdcLess60 + totalPdcLess90 +
					totalPdcLess120 + totalPdcMore120;

				object.current_amount -= totalPdcCurrent;
				object.due_less_30 -= totalPdcLess30;
				object.due_less_60 -= totalPdcLess60;
				object.due_less_90 -= totalPdcLess90;
				object.due_less_120 -= totalPdcLess120;
				object.due_more_120 -= totalPdcMore120;
				object.due_amount -= totalPdcDue;
				object.total_outstanding =
					totalOutstanding - totalPdcCurrent - totalPdcDue - unappliedAmount;

				// Cell variants for highlighting
				if (totalPdcLess30 > 0) { object._cellVariants.due_less_30 = 'pdc-applied'; }
				if (totalPdcLess60 > 0) { object._cellVariants.due_less_60 = 'pdc-applied'; }
				if (totalPdcLess90 > 0) { object._cellVariants.due_less_90 = 'pdc-applied'; }
				if (totalPdcLess120 > 0) { object._cellVariants.due_less_120 = 'pdc-applied'; }
				if (totalPdcMore120 > 0) { object._cellVariants.due_more_120 = 'pdc-applied'; }
				if (totalPdcCurrent > 0) { object._cellVariants.current_amount = 'pdc-applied'; }
				if (totalPdcDue > 0) { object._cellVariants.due_amount = 'pdc-applied'; }
				if (totalPdcDue > 0 || totalPdcCurrent > 0) {
					object._cellVariants.total_outstanding = 'pdc-applied';
				}
			}

			// Credit term
			if (groupedGlobal[code]) {
				object.credit_term = groupedGlobal[code].creditTerm;
			}

			// Build detail items
			const koPdcCards2 = koCards2.filter((ko) => ko.koDate > nowMs);
			const detailResult: any[] = [];

			for (const ivdn of cards) {
				const pdcAmount = koPdcCards2
					.filter((ko) => ko.toDocNo === ivdn.docNo)
					.reduce((a, b) => a + b.amount, 0);

				const koAmount = koCards2
					.filter((ko) => ko.toDocNo === ivdn.docNo && ko.koDate < nowMs)
					.reduce((a, b) => a + b.amount, 0);

				const gainLossAmount = koCards2
					.filter((ko) => ko.toDocNo === ivdn.docNo && ko.koDate < nowMs)
					.reduce((a, b) => a + b.gainLoss, 0);

				const outstandingAmount = ivdn.amount - koAmount - gainLossAmount;

				if (Math.round(outstandingAmount) === 0) { continue; }

				// Due date calculation: docDate + 1 month, end of month
				const calculatedDueDate = moment(ivdn.date)
					.add(1, 'month')
					.endOf('month')
					.valueOf();

				detailResult.push({
					code,
					doc_no: ivdn.docNo,
					doc_date: ivdn.date,
					due_date: calculatedDueDate,
					pdc: pdcAmount,
					outstanding_amount: outstandingAmount,
					outstanding_after_pdc: outstandingAmount - pdcAmount,
					days_due: moment(asOfDate).diff(calculatedDueDate, 'day'),
				});

				object.total_pdc += pdcAmount;
			}

			object.items = detailResult;
			result.push(object);
		}

		this.generateLoadingText(0.95);

		// Calculate totals
		const dueSum = result.reduce((a, b) => a + b.due_amount, 0);
		const currentSum = result.reduce((a, b) => a + b.current_amount, 0);
		const unappliedSum = result.reduce((a, b) => a + b.unapplied_amount, 0);

		this.dueSum = dueSum;
		this.currentSum = currentSum;
		this.outstandingSum = dueSum + currentSum;
		this.unappliedSum = unappliedSum;

		this.periodTotalData = [
			{
				current_sum:
					this.currency +
					' ' +
					addComma(this.currentSum, gdbx.numDecimal, true),
				due_sum:
					this.currency + ' ' + addComma(this.dueSum, gdbx.numDecimal, true),
				unapplied_sum:
					this.currency +
					' ' +
					addComma(this.unappliedSum, gdbx.numDecimal, true),
				outstanding_sum:
					this.currency +
					' ' +
					addComma(this.outstandingSum, gdbx.numDecimal, true),
			},
		];

		this.tableFields = [
			{ key: 'company_name', sortable: true },
			{ key: 'credit_term', sortable: true },
			{
				key: 'current_amount',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
			{
				key: 'due_less_30',
				label: 'Overdue 0-30',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
			{
				key: 'due_less_60',
				label: 'Overdue 31-60',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
			{
				key: 'due_less_90',
				label: 'Overdue 61-90',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
			{
				key: 'due_less_120',
				label: 'Overdue 91-120',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
			{
				key: 'due_more_120',
				label: 'Overdue > 120',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
			{
				key: 'due_amount',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
			{
				key: 'unapplied_amount',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
			{
				key: 'total_outstanding',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
		];

		this.detailFields = [
			{ key: 'doc_no', sortable: true },
			{
				key: 'doc_date',
				sortable: true,
				formatter: TableItemFormatter.date3,
			},
			{
				key: 'due_date',
				sortable: true,
				formatter: TableItemFormatter.date3,
			},
			{
				key: 'outstanding_amount',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
			{
				key: 'pdc',
				label: 'PDC',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
			{
				key: 'outstanding_after_pdc',
				label: 'Outstanding After PDC',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
			{ key: 'days_due', sortable: true },
		];

		this.tableItems = result.filter(
			(item) => Math.round(item.total_outstanding) !== 0,
		);

		this.generateLoadingText(1);
		this.saveHistory(
			'tableItems',
			'tableFields',
			'detailFields',
			'periodTotalData',
		);
	}
}
